import i18n from "../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatCurrency, formatNumber } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableHead, TableHeader, TableRow, TableCell, TableBody } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { QRCodeSVG } from "qrcode.react";
import jsPDF from "jspdf";
import { Boxes, Plus, AlertTriangle, RefreshCw, Search, Package, Barcode, ScanSearch, Warehouse, ShieldAlert, Layers3, HardHat, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { farmDateIso } from "@/lib/farmTime";
import { canDeleteInventoryItem } from "@/lib/inventoryPermissions";

const localApi = api;

const CATEGORY_OPTIONS = [
    ["medicine", "Medicine"],
    ["spare_parts", "Equipment Inventory"],
    ["tools", "Tools Inventory"],
    ["cleaning_supplies", "Cleaning / Tools"],
    ["office_supplies", "Office Supplies"],
    ["harvest_stock", "Harvest Stock"],
    ["miscellaneous", "Other"],
];

const TRANSACTION_TYPE_OPTIONS = [
    ["stock_in", "Stock In"],
    ["stock_out", "Stock Out"],
    ["stock_transfer", "Stock Transfer"],
    ["stock_adjustment", "Stock Adjustment"],
    ["returns", "Return"],
    ["waste", "Waste"],
    ["disposal", "Disposal"],
];

const STATUS_OPTIONS = ["active", "inactive", "quarantine", "expired"];
const ASSET_TYPE_OPTIONS = ["vehicle", "machinery", "tool", "electrical", "plumbing", "building", "other"];
const ASSET_STATUS_OPTIONS = ["in_use", "idle", "in_repair", "on_loan", "retired", "lost"];
const ASSET_CONDITION_OPTIONS = ["excellent", "good", "fair", "poor"];

function MetricCard({ label, value, sub, icon: Icon, tone = "default" }) {
    const tones = {
        default: "",
        good: "bg-primary/5 border-primary/20",
        warn: "bg-amber-500/5 border-amber-500/20",
        bad: "bg-destructive/5 border-destructive/20",
    };
    return (
        <Card className={`p-4 ${tones[tone]}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="tiny-label truncate">{label}</div>
                    <div className="mt-2 text-2xl font-bold font-mono truncate">{value}</div>
                    {sub ? <div className="mt-1 text-xs text-muted-foreground truncate">{sub}</div> : null}
                </div>
                {Icon ? <Icon className="w-5 h-5 text-primary shrink-0" /> : null}
            </div>
        </Card>
    );
}

function EmptyState({ title, description }) {
    return (
        <Card className="p-6 text-center text-sm text-muted-foreground">
            <div className="font-semibold text-foreground">{title}</div>
            <div className="mt-1">{description}</div>
        </Card>
    );
}

function uid(prefix) {
    return `${prefix}-${farmDateIso().replace(/-/g, "")}-${crypto.randomUUID()}`;
}

export default function InventoryPage() {
    const { can, user } = useAuth();
    const canManageInventory = can("manage_inventory");
    const canDeleteInventory = canDeleteInventoryItem(user);
    const canManageAssets = can("manage_settings");

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [farm, setFarm] = useState({ currency: "PHP" });
    const [items, setItems] = useState([]);
    const [movements, setMovements] = useState([]);
    const [assets, setAssets] = useState([]);
    const [metrics, setMetrics] = useState({});
    const [tab, setTab] = useState("overview");
    const [itemDlg, setItemDlg] = useState(null);
    const [assetDlg, setAssetDlg] = useState(null);
    const [transactionDlg, setTransactionDlg] = useState(null);
    const [search, setSearch] = useState("");
    const [locationFilter, setLocationFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [qrQuery, setQrQuery] = useState("");
    const [qrLookup, setQrLookup] = useState(null);
    const [qrPreview, setQrPreview] = useState(null);
    const [qrMode, setQrMode] = useState("qr");
    const [deleteItemTarget, setDeleteItemTarget] = useState(null);
    const [deletingItem, setDeletingItem] = useState(false);

    const confirmDeleteItem = async () => {
        if (!deleteItemTarget || deletingItem) return;
        setDeletingItem(true);
        try {
            await localApi.delete(`/inventory-items/${deleteItemTarget.id}`);
            setItems((current) => current.filter((item) => item.id !== deleteItemTarget.id));
            setDeleteItemTarget(null);
            toast.success("Inventory item deleted");
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Inventory item could not be deleted");
        } finally {
            setDeletingItem(false);
        }
    };

    const deleteButton = (item) => canDeleteInventory ? (
        <Button
            size="sm"
            variant="destructive"
            onClick={() => setDeleteItemTarget(item)}
            data-testid={`delete-inventory-item-${item.id}`}
        >
            <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
        </Button>
    ) : null;

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const metricKeys = ["total_inventory_value", "low_stock_items", "out_of_stock_items", "expiring_inventory_items", "feed_usage_this_month", "medicine_usage_this_month", "inventory_consumption_this_month"];
            const [farmResp, itemsResp, movementResp, assetResp, metricValues] = await Promise.all([
                localApi.get("/farm").then((r) => r.data).catch(() => ({ currency: "PHP" })),
                localApi.get("/inventory-items").then((r) => r.data),
                localApi.get("/inventory-movements").then((r) => r.data).catch(() => []),
                localApi.get("/farm-assets").then((r) => r.data).catch(() => []),
                Promise.all(metricKeys.map((key) => localApi.get(`/metrics/${key}`).then((r) => [key, r.data?.value ?? 0]).catch(() => [key, 0]))),
            ]);
            setFarm(farmResp || { currency: "PHP" });
            setItems(itemsResp || []);
            setMovements(movementResp || []);
            setAssets(Array.isArray(assetResp) ? assetResp : []);
            setMetrics(Object.fromEntries(metricValues));
        } catch (err) {
            setError(err.response?.data?.detail || err.message || "Failed to load inventory management");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const cur = (value) => formatCurrency(value || 0, farm.currency || "PHP");
    const allItems = useMemo(() => items || [], [items]);
    const searchableItems = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return allItems.filter((item) => {
            if (categoryFilter !== "all" && item.category !== categoryFilter) return false;
            if (locationFilter !== "all" && (item.storage_location || "unassigned") !== locationFilter) return false;
            if (!needle) return true;
            return [item.name, item.sku, item.supplier, item.storage_location, item.notes, item.batch_number, item.status].join(" ").toLowerCase().includes(needle);
        });
    }, [allItems, categoryFilter, locationFilter, search]);

    const medicineItems = useMemo(() => searchableItems.filter((item) => ["medicine", "vaccine"].includes(item.category)), [searchableItems]);
    const equipmentItems = useMemo(() => searchableItems.filter((item) => ["spare_parts", "equipment_parts"].includes(item.category)), [searchableItems]);
    const toolsItems = useMemo(() => searchableItems.filter((item) => ["tools", "farm_supplies", "cleaning_supplies"].includes(item.category)), [searchableItems]);
    const lowStockItems = useMemo(() => searchableItems.filter((item) => Number(item.quantity || 0) <= Number(item.reorder_level || 0)), [searchableItems]);
    const expiringItems = useMemo(() => searchableItems.filter((item) => ["expiring_soon", "expired"].includes(item.expiry_status)), [searchableItems]);
    const expiredItems = useMemo(() => searchableItems.filter((item) => item.expiry_status === "expired"), [searchableItems]);
    const categorySummary = useMemo(() => {
        const counts = new Map();
        searchableItems.forEach((item) => {
            const key = item.category || "miscellaneous";
            const bucket = counts.get(key) || { category: key, items: 0, quantity: 0, value: 0 };
            bucket.items += 1;
            bucket.quantity += Number(item.quantity || 0);
            bucket.value += Number(item.inventory_value || 0);
            counts.set(key, bucket);
        });
        return [...counts.values()].sort((a, b) => (b.value || 0) - (a.value || 0));
    }, [searchableItems]);
    const locations = useMemo(() => {
        const map = new Map();
        searchableItems.forEach((item) => {
            const key = item.storage_location || "Unassigned";
            const current = map.get(key) || { name: key, items: 0, stock: 0, value: 0, low: 0, expiring: 0 };
            current.items += 1;
            current.stock += Number(item.quantity || 0);
            current.value += Number(item.inventory_value || 0);
            if (item.low_stock) current.low += 1;
            if (["expiring_soon", "expired"].includes(item.expiry_status)) current.expiring += 1;
            map.set(key, current);
        });
        return [...map.values()].sort((a, b) => (b.value || 0) - (a.value || 0));
    }, [searchableItems]);

    const movementRows = useMemo(() => [...movements].sort((a, b) => `${b.date || ""}T${b.created_at || ""}`.localeCompare(`${a.date || ""}T${a.created_at || ""}`)).slice(0, 20), [movements]);
    const assetRows = useMemo(() => [...assets].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")), [assets]);

    const selectedQrData = useMemo(() => {
        if (!qrQuery.trim()) return null;
        const needle = qrQuery.trim().toLowerCase();
        const inventoryMatch = searchableItems.find((item) => [item.sku, item.id, item.name].join(" ").toLowerCase().includes(needle));
        if (inventoryMatch) return { kind: "inventory", code: inventoryMatch.sku || inventoryMatch.id, title: inventoryMatch.name, subtitle: `${inventoryMatch.category} · ${inventoryMatch.storage_location || "No location"}` };
        const assetMatch = assetRows.find((asset) => [asset.qr_code, asset.qrCode, asset.id, asset.name].join(" ").toLowerCase().includes(needle));
        if (assetMatch) return { kind: "asset", code: assetMatch.qr_code || assetMatch.qrCode || assetMatch.id, title: assetMatch.name, subtitle: `${assetMatch.type || "asset"} · ${assetMatch.status || "unknown"}` };
        return null;
    }, [assetRows, qrQuery, searchableItems]);

    useEffect(() => {
        setQrLookup(selectedQrData);
    }, [selectedQrData]);

    const saveItem = async () => {
        if (!itemDlg?.name?.trim()) return toast.error("Item name is required");
        try {
            const sku = itemDlg.sku?.trim() || uid("INV");
            const body = {
                ...itemDlg,
                sku,
                category: itemDlg.category || "miscellaneous",
                quantity: Number(itemDlg.quantity) || 0,
                minimum_stock: Number(itemDlg.minimum_stock) || 0,
                reorder_level: Number(itemDlg.reorder_level) || 0,
                cost_per_unit: Number(itemDlg.cost_per_unit) || 0,
            };
            if (itemDlg.id) await localApi.put(`/inventory-items/${itemDlg.id}`, body); else await localApi.post("/inventory-items", body);
            toast.success("Inventory item saved");
            setItemDlg(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save inventory item");
        }
    };

    const saveAsset = async () => {
        if (!assetDlg?.name?.trim()) return toast.error("Asset name is required");
        try {
            const body = {
                ...assetDlg,
                type: assetDlg.type || "other",
                status: assetDlg.status || "in_use",
                condition: assetDlg.condition || "good",
                next_service_date: assetDlg.next_service_date || undefined,
                qr_code: assetDlg.qr_code?.trim() || assetDlg.qrCode?.trim() || uid("QR"),
            };
            if (assetDlg.id) await localApi.put(`/farm-assets/${assetDlg.id}`, body); else await localApi.post("/farm-assets", body);
            toast.success("Asset saved");
            setAssetDlg(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save asset");
        }
    };

    const deleteAsset = async (asset) => {
        if (!asset?.id) return;
        const confirmed = window.confirm(`Delete asset "${asset.name || asset.id}"? This cannot be undone.`);
        if (!confirmed) return;
        try {
            await localApi.delete(`/farm-assets/${asset.id}`);
            toast.success("Asset deleted");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete asset");
        }
    };

    const saveTransaction = async () => {
        if (!transactionDlg?.item_id) return toast.error("Select an inventory item");
        const movementType = transactionDlg.type || "stock_in";
        const qty = Number(transactionDlg.quantity) || 0;
        const delta = Number(transactionDlg.delta) || 0;

        if (movementType === "stock_adjustment") {
            if (Math.abs(delta) < 1e-9 && qty <= 0) {
                return toast.error("Provide adjustment delta or quantity");
            }
        } else if (qty <= 0) {
            return toast.error("Quantity must be greater than 0");
        }

        try {
            const selectedItem = allItems.find((item) => item.id === transactionDlg.item_id);
            const nowStamp = Date.now();
            const payload = {
                item_id: transactionDlg.item_id,
                item_name: selectedItem?.name,
                category: selectedItem?.category,
                type: movementType,
                quantity: Math.abs(qty),
                unit: transactionDlg.unit || selectedItem?.unit || "unit",
                date: transactionDlg.date || farmDateIso(),
                from_location: transactionDlg.from_location || undefined,
                to_location: transactionDlg.to_location || undefined,
                reason: transactionDlg.reason || undefined,
                notes: transactionDlg.notes || "",
                allow_negative_stock: !!transactionDlg.allow_negative_stock,
                source_module: "inventory_ui",
                source_record_id: `inv-ui-${nowStamp}`,
                source_submission_id: transactionDlg.source_submission_id || uid("inv-ui"),
            };
            if (movementType === "stock_adjustment" && Math.abs(delta) > 0) {
                payload.delta = delta;
            }
            await localApi.post("/inventory-transactions", payload);
            toast.success("Inventory transaction recorded");
            setTransactionDlg(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to record transaction");
        }
    };

    const printCode = (payload, mode = "qr") => {
        const popup = window.open("", "_blank", "width=640,height=720");
        if (!popup) return;
        const codeText = payload?.code || "";
        const isBarcode = mode === "barcode";
        const label = isBarcode ? "Barcode" : "QR";
        popup.document.write(`<!doctype html><html><head><title>${label}</title><style>body{font-family:sans-serif;display:grid;place-items:center;min-height:100vh;margin:0}.box{padding:24px;border:1px solid #ddd;border-radius:16px;text-align:center}.code{margin-top:14px;max-width:100%}</style></head><body><div class="box"><h1>${payload.title || label}</h1><p>${payload.subtitle || ""}</p><div id="code-wrap" class="code"></div><div style="font-family:monospace;margin-top:12px">${codeText}</div></div></body></html>`);
        popup.document.close();
        if (isBarcode) {
            const img = popup.document.createElement("img");
            img.alt = "Barcode";
            img.src = `https://bwipjs-api.metafloor.com/?bcid=code128&includetext&scale=2&text=${encodeURIComponent(codeText)}`;
            const wrap = popup.document.getElementById("code-wrap");
            if (wrap) wrap.appendChild(img);
        } else {
            const encoded = encodeURIComponent(codeText);
            const url = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encoded}`;
            const img = popup.document.createElement("img");
            img.src = url;
            img.alt = "QR Code";
            const wrap = popup.document.getElementById("code-wrap");
            if (wrap) wrap.appendChild(img);
        }
        popup.focus();
    };

    const barcodeImageUrl = (code) => `https://bwipjs-api.metafloor.com/?bcid=code128&includetext&scale=2&text=${encodeURIComponent(code || "")}`;

    const svgToPngDataUrl = async (svgElement) => {
        if (!svgElement) return null;
        const svgText = new XMLSerializer().serializeToString(svgElement);
        const svgBlob = new Blob([svgText], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(svgBlob);
        try {
            const img = new Image();
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = url;
            });
            const canvas = document.createElement("canvas");
            canvas.width = img.width || 512;
            canvas.height = img.height || 512;
            const ctx = canvas.getContext("2d");
            if (!ctx) return null;
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            return canvas.toDataURL("image/png");
        } finally {
            URL.revokeObjectURL(url);
        }
    };

    const downloadCodePng = async () => {
        if (!qrPreview) return;
        if (qrMode === "barcode") {
            const a = document.createElement("a");
            a.href = barcodeImageUrl(qrPreview.code);
            a.download = `${qrPreview.title || "inventory-code"}-barcode.png`;
            a.target = "_blank";
            a.click();
            return;
        }
        const svgNode = document.getElementById("inventory-qr-svg");
        const dataUrl = await svgToPngDataUrl(svgNode);
        if (!dataUrl) return;
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `${qrPreview.title || "inventory-code"}-qr.png`;
        a.click();
    };

    const downloadCodePdf = async () => {
        if (!qrPreview) return;
        let dataUrl = null;
        if (qrMode === "barcode") {
            try {
                const res = await fetch(barcodeImageUrl(qrPreview.code));
                const blob = await res.blob();
                dataUrl = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(blob);
                });
            } catch (_err) {
                toast.error("Could not export barcode PDF");
                return;
            }
        } else {
            const svgNode = document.getElementById("inventory-qr-svg");
            dataUrl = await svgToPngDataUrl(svgNode);
            if (!dataUrl) return;
        }
        const pdf = new jsPDF({ unit: "pt", format: "a4" });
        pdf.setFontSize(14);
        pdf.text(qrPreview.title || "Inventory Code", 40, 42);
        pdf.setFontSize(10);
        pdf.text(qrPreview.subtitle || "", 40, 60);
        const width = 260;
        const height = qrMode === "barcode" ? 120 : 260;
        pdf.addImage(dataUrl, "PNG", 40, 80, width, height);
        pdf.setFontSize(10);
        pdf.text(`Code: ${qrPreview.code || ""}`, 40, 80 + height + 18);
        pdf.save(`${qrPreview.title || "inventory-code"}-${qrMode}.pdf`);
    };

    const printQr = (payload) => {
        printCode(payload, "qr");
    };

    const categoryCounts = {
        medicine: medicineItems.length,
        equipment: equipmentItems.length,
        tools: toolsItems.length,
    };

    if (loading) return <div className="p-8 text-center text-muted-foreground">{i18n.t("batch2.d758262e8fe0")}</div>;
    if (error) {
        return (
            <Card className="p-6 border-destructive/40 bg-destructive/5" data-testid="inventory-error">
                <div className="font-semibold text-destructive">{i18n.t("batch2.3dcb2be9fa88")}</div>
                <div className="text-sm text-muted-foreground mt-1">{error}</div>
                <Button className="mt-4" variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-2" />{i18n.t("batch2.9f5cd8a2e880")}</Button>
            </Card>
        );
    }

    return (
        <div className="space-y-6" data-testid="inventory-page">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <div className="tiny-label">{i18n.t("batch2.ae65324b857d")}</div>
                    <h1 className="text-4xl font-bold tracking-tight mt-2">{i18n.t("batch2.3c40a44bb41f")}</h1>
                    <p className="text-muted-foreground mt-2">{i18n.t("batch2.2044b2d32473")}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-2" />{i18n.t("batch2.56e3badc4e6c")}</Button>
                    <Dialog open={!!itemDlg} onOpenChange={(open) => !open && setItemDlg(null)}>
                        <DialogTrigger asChild>
                            <Button onClick={() => setItemDlg({ name: "", category: "medicine", sku: "", unit: "pcs", quantity: 0, minimum_stock: 0, reorder_level: 0, cost_per_unit: 0, supplier: "", expiry_date: "", batch_number: "", storage_location: "", status: "active", notes: "" })} data-testid="add-inventory-btn" disabled={!canManageInventory}>
                                <Plus className="w-4 h-4 mr-2" />{i18n.t("batch2.78b819622818")}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl">
                            <DialogHeader><DialogTitle>{itemDlg?.id ? "Edit" : "New"} {i18n.t("batch2.c81b853cafb4")}</DialogTitle></DialogHeader>
                            {itemDlg ? (
                                <div className="space-y-4">
                                    <div className="grid gap-3 md:grid-cols-2">
                                        <div><Label>{i18n.t("batch2.d145bb830936")}</Label><Input value={itemDlg.name} onChange={(e) => setItemDlg({ ...itemDlg, name: e.target.value })} className="h-11 mt-1" data-testid="inv-name-input" /></div>
                                        <div><Label>{i18n.t("batch2.a3c686e711e4")}</Label>
                                            <Select value={itemDlg.category} onValueChange={(value) => setItemDlg({ ...itemDlg, category: value })}>
                                                <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                                                <SelectContent>{CATEGORY_OPTIONS.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div><Label>{i18n.t("batch2.d59192b9c8f2")}</Label><Input value={itemDlg.sku} onChange={(e) => setItemDlg({ ...itemDlg, sku: e.target.value })} className="h-11 mt-1 font-mono" placeholder={i18n.t("batch2.7451228fe603")} /></div>
                                        <div><Label>{i18n.t("batch2.f6b935ab3371")}</Label><Input value={itemDlg.unit} onChange={(e) => setItemDlg({ ...itemDlg, unit: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("batch2.44f6af694554")}</Label><Input type="number" min="0" value={itemDlg.quantity} onChange={(e) => setItemDlg({ ...itemDlg, quantity: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("batch2.931b0211b3e6")}</Label><Input type="number" min="0" value={itemDlg.minimum_stock || 0} onChange={(e) => setItemDlg({ ...itemDlg, minimum_stock: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("batch2.59ef31e37acc")}</Label><Input type="number" min="0" value={itemDlg.reorder_level} onChange={(e) => setItemDlg({ ...itemDlg, reorder_level: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("batch2.9daa9201229d")}</Label><Input type="number" min="0" step="0.01" value={itemDlg.cost_per_unit} onChange={(e) => setItemDlg({ ...itemDlg, cost_per_unit: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("batch2.55edd462872a")}</Label><Input value={itemDlg.supplier} onChange={(e) => setItemDlg({ ...itemDlg, supplier: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("batch2.86b217fdcad3")}</Label><Input value={itemDlg.batch_number} onChange={(e) => setItemDlg({ ...itemDlg, batch_number: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("batch2.8d007050838a")}</Label><Input type="date" value={itemDlg.expiry_date || ""} onChange={(e) => setItemDlg({ ...itemDlg, expiry_date: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("batch2.a225c8ebcac0")}</Label><Input value={itemDlg.storage_location} onChange={(e) => setItemDlg({ ...itemDlg, storage_location: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("batch2.bae7d5be7082")}</Label>
                                            <Select value={itemDlg.status || "active"} onValueChange={(value) => setItemDlg({ ...itemDlg, status: value })}>
                                                <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                                                <SelectContent>{STATUS_OPTIONS.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div><Label>{i18n.t("batch2.70440046a3dc")}</Label><Input value={itemDlg.notes || ""} onChange={(e) => setItemDlg({ ...itemDlg, notes: e.target.value })} className="h-11 mt-1" /></div>
                                    <Button onClick={saveItem} className="w-full h-11" data-testid="save-inv-btn">{i18n.t("batch2.ea0e082d4d4b")}</Button>
                                </div>
                            ) : null}
                        </DialogContent>
                    </Dialog>
                    <Dialog open={!!assetDlg} onOpenChange={(open) => !open && setAssetDlg(null)}>
                        <DialogTrigger asChild>
                            <Button variant="outline" onClick={() => setAssetDlg({ name: "", type: "tool", category: "", status: "in_use", condition: "good", location: "", next_service_date: "", qr_code: "", notes: "" })} data-testid="add-asset-btn" disabled={!canManageAssets}>
                                <HardHat className="w-4 h-4 mr-2" />{i18n.t("batch2.d356631d4242")}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl">
                            <DialogHeader><DialogTitle>{assetDlg?.id ? "Edit" : "New"} {i18n.t("batch2.4426afd90a77")}</DialogTitle></DialogHeader>
                            {assetDlg ? (
                                <div className="space-y-4">
                                    <div className="grid gap-3 md:grid-cols-2">
                                        <div><Label>{i18n.t("batch2.d145bb830936")}</Label><Input value={assetDlg.name} onChange={(e) => setAssetDlg({ ...assetDlg, name: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("batch2.3deb74565196")}</Label>
                                            <Select value={assetDlg.type} onValueChange={(value) => setAssetDlg({ ...assetDlg, type: value })}>
                                                <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                                                <SelectContent>{ASSET_TYPE_OPTIONS.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div><Label>{i18n.t("batch2.bae7d5be7082")}</Label>
                                            <Select value={assetDlg.status} onValueChange={(value) => setAssetDlg({ ...assetDlg, status: value })}>
                                                <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                                                <SelectContent>{ASSET_STATUS_OPTIONS.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div><Label>{i18n.t("batch2.2f4979324e03")}</Label>
                                            <Select value={assetDlg.condition} onValueChange={(value) => setAssetDlg({ ...assetDlg, condition: value })}>
                                                <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                                                <SelectContent>{ASSET_CONDITION_OPTIONS.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div><Label>{i18n.t("batch2.d219c68101f5")}</Label><Input value={assetDlg.location || assetDlg.storage_location || ""} onChange={(e) => setAssetDlg({ ...assetDlg, location: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("batch2.2d2a1342f45d")}</Label><Input type="date" value={assetDlg.next_service_date || ""} onChange={(e) => setAssetDlg({ ...assetDlg, next_service_date: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("batch2.d65ae9967727")}</Label><Input value={assetDlg.qr_code || assetDlg.qrCode || ""} onChange={(e) => setAssetDlg({ ...assetDlg, qr_code: e.target.value })} className="h-11 mt-1 font-mono" /></div>
                                        <div><Label>{i18n.t("batch2.a3c686e711e4")}</Label><Input value={assetDlg.category || ""} onChange={(e) => setAssetDlg({ ...assetDlg, category: e.target.value })} className="h-11 mt-1" /></div>
                                    </div>
                                    <div><Label>{i18n.t("batch2.70440046a3dc")}</Label><Input value={assetDlg.notes || ""} onChange={(e) => setAssetDlg({ ...assetDlg, notes: e.target.value })} className="h-11 mt-1" /></div>
                                    <Button onClick={saveAsset} className="w-full h-11" disabled={!canManageAssets} data-testid="save-asset-btn">{i18n.t("batch2.ffc91ed29d9d")}</Button>
                                </div>
                            ) : null}
                        </DialogContent>
                    </Dialog>
                    <Dialog open={!!transactionDlg} onOpenChange={(open) => !open && setTransactionDlg(null)}>
                        <DialogTrigger asChild>
                            <Button
                                variant="outline"
                                onClick={() => setTransactionDlg({
                                    item_id: searchableItems[0]?.id || "",
                                    type: "stock_in",
                                    quantity: "",
                                    delta: "",
                                    unit: searchableItems[0]?.unit || "unit",
                                    date: farmDateIso(),
                                    source_submission_id: uid("inventory-movement"),
                                    from_location: "",
                                    to_location: "",
                                    reason: "",
                                    notes: "",
                                    allow_negative_stock: false,
                                })}
                                data-testid="inventory-transaction-btn"
                                disabled={!canManageInventory || searchableItems.length === 0}
                            >
                                <Plus className="w-4 h-4 mr-2" />{i18n.t("batch2.1a7b7c1b33d1")}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl">
                            <DialogHeader><DialogTitle>{i18n.t("batch2.7b83bbb85e1b")}</DialogTitle></DialogHeader>
                            {transactionDlg ? (
                                <div className="space-y-4">
                                    <div className="grid gap-3 md:grid-cols-2">
                                        <div><Label>{i18n.t("batch2.3700890937a4")}</Label>
                                            <Select value={transactionDlg.item_id} onValueChange={(value) => {
                                                const selected = searchableItems.find((row) => row.id === value);
                                                setTransactionDlg({ ...transactionDlg, item_id: value, unit: selected?.unit || transactionDlg.unit || "unit" });
                                            }}>
                                                <SelectTrigger className="h-11 mt-1"><SelectValue placeholder={i18n.t("batch2.b0946c471bef")} /></SelectTrigger>
                                                <SelectContent>{searchableItems.map((item) => <SelectItem key={item.id} value={item.id}>{item.name} ({item.sku || item.id})</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div><Label>{i18n.t("batch2.9afdc0c98e0e")}</Label>
                                            <Select value={transactionDlg.type} onValueChange={(value) => setTransactionDlg({ ...transactionDlg, type: value })}>
                                                <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                                                <SelectContent>{TRANSACTION_TYPE_OPTIONS.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </div>
                                        <div><Label>{i18n.t("batch2.44f6af694554")} {transactionDlg.type === "stock_adjustment" ? "(optional)" : "*"}</Label><Input type="number" min="0" step="0.01" value={transactionDlg.quantity} onChange={(e) => setTransactionDlg({ ...transactionDlg, quantity: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("batch2.a4cbd21f4a3d")} {transactionDlg.type === "stock_adjustment" ? "*" : "(optional)"}</Label><Input type="number" step="0.01" value={transactionDlg.delta} onChange={(e) => setTransactionDlg({ ...transactionDlg, delta: e.target.value })} className="h-11 mt-1" placeholder={i18n.t("batch2.7477762fadeb")} /></div>
                                        <div><Label>{i18n.t("batch2.f6b935ab3371")}</Label><Input value={transactionDlg.unit} onChange={(e) => setTransactionDlg({ ...transactionDlg, unit: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("batch2.eb9a4bc1c0c1")}</Label><Input type="date" value={transactionDlg.date} onChange={(e) => setTransactionDlg({ ...transactionDlg, date: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("batch2.e1dee3f10e02")}</Label><Input value={transactionDlg.from_location} onChange={(e) => setTransactionDlg({ ...transactionDlg, from_location: e.target.value })} className="h-11 mt-1" /></div>
                                        <div><Label>{i18n.t("batch2.232dc1368f6e")}</Label><Input value={transactionDlg.to_location} onChange={(e) => setTransactionDlg({ ...transactionDlg, to_location: e.target.value })} className="h-11 mt-1" /></div>
                                    </div>
                                    <div><Label>{i18n.t("batch2.f219cc0614ae")}</Label><Input value={transactionDlg.reason} onChange={(e) => setTransactionDlg({ ...transactionDlg, reason: e.target.value })} className="h-11 mt-1" placeholder={i18n.t("batch2.33f147e5a87f")} /></div>
                                    <div><Label>{i18n.t("batch2.70440046a3dc")}</Label><Input value={transactionDlg.notes} onChange={(e) => setTransactionDlg({ ...transactionDlg, notes: e.target.value })} className="h-11 mt-1" /></div>
                                    <label className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={!!transactionDlg.allow_negative_stock}
                                            onChange={(e) => setTransactionDlg({ ...transactionDlg, allow_negative_stock: e.target.checked })}
                                        />
                                        {i18n.t("batch2.5b779ea02219")}
                                    </label>
                                    <Button onClick={saveTransaction} className="w-full h-11" data-testid="save-inventory-transaction-btn">{i18n.t("batch2.a3f812e5f85c")}</Button>
                                </div>
                            ) : null}
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="inventory-kpis">
                <MetricCard label={i18n.t("batch2.d96f52b84799")} value={cur(metrics.total_inventory_value || 0)} icon={Boxes} />
                <MetricCard label={i18n.t("batch2.023d1181ebc6")} value={formatNumber(metrics.low_stock_items || lowStockItems.length)} icon={AlertTriangle} tone={lowStockItems.length > 0 ? "bad" : "default"} />
                <MetricCard label={i18n.t("batch2.bc67a8aefae6")} value={formatNumber(metrics.out_of_stock_items || 0)} icon={ShieldAlert} tone={Number(metrics.out_of_stock_items || 0) > 0 ? "bad" : "default"} />
                <MetricCard label={i18n.t("batch2.b98d67213b38")} value={formatNumber(metrics.expiring_inventory_items || expiringItems.length)} icon={AlertTriangle} tone={expiringItems.length > 0 ? "warn" : "default"} />
                <MetricCard label={i18n.t("batch2.a689a999a5e6")} value={formatNumber(expiredItems.length)} icon={ShieldAlert} tone={expiredItems.length > 0 ? "bad" : "default"} />
                <MetricCard label={i18n.t("batch2.cd8c2df26c59")} value={formatNumber(categoryCounts.medicine)} icon={Package} />
                <MetricCard label={i18n.t("batch2.36155d48f460")} value={formatNumber(categoryCounts.equipment)} icon={HardHat} />
                <MetricCard label={i18n.t("batch2.4fa8cc860c52")} value={formatNumber(categoryCounts.tools)} icon={Warehouse} />
                <MetricCard label={i18n.t("batch2.20e338624cee")} value={formatNumber(assetRows.length)} icon={Layers3} />
            </div>

            {(lowStockItems.length > 0 || expiringItems.length > 0) && (
                <div className="grid gap-4 md:grid-cols-2">
                    <Card className="p-4 border-destructive/30 bg-destructive/5">
                        <div className="font-semibold text-destructive flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{i18n.t("batch2.b452abc00d55")}</div>
                        <div className="mt-3 space-y-2">
                            {lowStockItems.slice(0, 4).map((item) => (
                                <div key={item.id} className="rounded-md border border-destructive/20 bg-background p-3 flex items-center justify-between gap-3 text-sm">
                                    <div className="min-w-0">
                                        <div className="font-semibold truncate">{item.name}</div>
                                        <div className="text-xs text-muted-foreground">{formatNumber(item.quantity)} {item.unit} {i18n.t("batch2.b2537e92cedd")} {formatNumber(item.reorder_level)}</div>
                                    </div>
                                    <Badge variant="outline" className="text-destructive border-destructive/30">{i18n.t("batch2.a124947cbd2d")}</Badge>
                                </div>
                            ))}
                        </div>
                    </Card>
                    <Card className="p-4 border-amber-500/30 bg-amber-500/5">
                        <div className="font-semibold text-amber-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{i18n.t("batch2.f6d8dc2e9c7a")}</div>
                        <div className="mt-3 space-y-2">
                            {expiringItems.slice(0, 4).map((item) => (
                                <div key={item.id} className="rounded-md border border-amber-500/20 bg-background p-3 flex items-center justify-between gap-3 text-sm">
                                    <div className="min-w-0">
                                        <div className="font-semibold truncate">{item.name}</div>
                                        <div className="text-xs text-muted-foreground">{item.expiry_date || "No expiry"} · {item.expiry_status.replace(/_/g, " ")}</div>
                                    </div>
                                    <Badge variant="outline">{item.expiry_status.replace(/_/g, " ")}</Badge>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="h-auto flex flex-wrap gap-1" data-testid="inventory-tabs">
                    <TabsTrigger value="overview">{i18n.t("batch2.0efc2e6be4c2")}</TabsTrigger>
                    <TabsTrigger value="items">{i18n.t("batch2.300d29fd5f32")}</TabsTrigger>
                    <TabsTrigger value="medicine">{i18n.t("batch2.cd8c2df26c59")}</TabsTrigger>
                    <TabsTrigger value="equipment">{i18n.t("batch2.36155d48f460")}</TabsTrigger>
                    <TabsTrigger value="tools">{i18n.t("batch2.4fa8cc860c52")}</TabsTrigger>
                    <TabsTrigger value="assets">{i18n.t("batch2.d356631d4242")}</TabsTrigger>
                    <TabsTrigger value="locations">{i18n.t("batch2.1d0c73d85f54")}</TabsTrigger>
                    <TabsTrigger value="movements">{i18n.t("batch2.1e75cf7a0372")}</TabsTrigger>
                    <TabsTrigger value="qr">{i18n.t("batch2.01a8dd48c500")}</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                    <Card className="p-4">
                        <div className="grid gap-3 md:grid-cols-4">
                            <div><Label>{i18n.t("batch2.bce06414177f")}</Label><Input value={search} onChange={(e) => setSearch(e.target.value)} className="h-11 mt-1" placeholder={i18n.t("batch2.1e1856a908fb")} /></div>
                            <div><Label>{i18n.t("batch2.a3c686e711e4")}</Label>
                                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                                    <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{i18n.t("batch2.060be00f4f26")}</SelectItem>
                                        {CATEGORY_OPTIONS.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div><Label>{i18n.t("batch2.e997447a5ce9")}</Label>
                                <Select value={locationFilter} onValueChange={setLocationFilter}>
                                    <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{i18n.t("batch2.797a4917230e")}</SelectItem>
                                        {locations.map((location) => <SelectItem key={location.name} value={location.name}>{location.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-end"><Button variant="outline" className="h-11 w-full" onClick={() => { setSearch(""); setLocationFilter("all"); setCategoryFilter("all"); }}><RefreshCw className="w-4 h-4 mr-2" />{i18n.t("batch2.381c4bf1d409")}</Button></div>
                        </div>
                    </Card>
                    <div className="grid gap-4 lg:grid-cols-2">
                        <Card className="p-4">
                            <div className="font-semibold mb-3">{i18n.t("batch2.f7aaca979af6")}</div>
                            <div className="space-y-3 text-sm">
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("batch2.0d51eeffd436")}</span><span className="font-semibold">{searchableItems.length}</span></div>
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("batch2.90fb871e3532")}</span><span className="font-semibold">{locations.length}</span></div>
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("batch2.e82e5b2a7359")}</span><span className="font-semibold">{movementRows.length}</span></div>
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("batch2.4e579c31017e")}</span><span className="font-semibold">{assetRows.length}</span></div>
                            </div>
                            <Progress className="mt-4" value={Math.min(100, Math.round((Number(metrics.low_stock_items || 0) / Math.max(searchableItems.length || 1, 1)) * 100))} />
                        </Card>
                        <Card className="p-4">
                            <div className="font-semibold mb-3">{i18n.t("batch2.dd5f667597ac")}</div>
                            {categorySummary.length === 0 ? <div className="text-sm text-muted-foreground">{i18n.t("batch2.a10cf6a84db4")}</div> : (
                                <div className="space-y-2">
                                    {categorySummary.slice(0, 6).map((row) => (
                                        <div key={row.category} className="text-sm flex items-center justify-between gap-3">
                                            <span className="capitalize truncate">{String(row.category || "miscellaneous").replace(/_/g, " ")}</span>
                                            <span className="font-mono">{formatNumber(row.quantity)} · {cur(row.value)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>
                    <div className="grid gap-4 lg:grid-cols-1">
                        <Card className="p-4">
                            <div className="font-semibold mb-3">{i18n.t("batch2.c40810f5c31d")}</div>
                            <div className="grid grid-cols-2 gap-3">
                                <Button variant="outline" onClick={() => setTab("medicine")}><Package className="w-4 h-4 mr-2" />{i18n.t("batch2.cd8c2df26c59")}</Button>
                                <Button variant="outline" onClick={() => setTab("equipment")}><HardHat className="w-4 h-4 mr-2" />{i18n.t("batch2.36155d48f460")}</Button>
                                <Button variant="outline" onClick={() => setTab("tools")}><Warehouse className="w-4 h-4 mr-2" />{i18n.t("batch2.4fa8cc860c52")}</Button>
                                <Button variant="outline" onClick={() => setTab("assets")}><Layers3 className="w-4 h-4 mr-2" />{i18n.t("batch2.20e338624cee")}</Button>
                            </div>
                        </Card>
                    </div>
                    <Card className="p-4">
                        <div className="font-semibold mb-3">{i18n.t("batch2.5332fb07ad51")}</div>
                        {searchableItems.length === 0 ? <EmptyState title={i18n.t("batch2.9c59b3b940b8")} description={i18n.t("batch2.725d63c76780")} /> : (
                            <Table>
                                <TableHeader><TableRow><TableHead>{i18n.t("batch2.709a23220f2c")}</TableHead><TableHead>{i18n.t("batch2.a3c686e711e4")}</TableHead><TableHead>{i18n.t("batch2.d59192b9c8f2")}</TableHead><TableHead className="text-right">{i18n.t("batch2.1e5ff9e500c2")}</TableHead><TableHead className="text-right">{i18n.t("batch2.33d9978c4477")}</TableHead><TableHead>{i18n.t("batch2.df4ebc16e8c5")}</TableHead><TableHead>{i18n.t("batch2.d219c68101f5")}</TableHead><TableHead>{i18n.t("batch2.55edd462872a")}</TableHead><TableHead /></TableRow></TableHeader>
                                <TableBody>
                                    {searchableItems.map((item) => (
                                        <TableRow key={item.id} className={item.low_stock ? "bg-destructive/5" : ""}>
                                            <TableCell className="font-semibold">{item.name}</TableCell>
                                            <TableCell><Badge variant="outline">{item.category}</Badge></TableCell>
                                            <TableCell className="font-mono text-xs">{item.sku || "—"}</TableCell>
                                            <TableCell className="text-right font-mono">{formatNumber(item.quantity)} {item.unit}{item.low_stock ? <AlertTriangle className="w-3 h-3 inline ml-1 text-destructive" /> : null}</TableCell>
                                            <TableCell className="text-right font-mono">{formatNumber(item.reorder_level)}</TableCell>
                                            <TableCell className="text-xs">{item.batch_number || "—"}<div className="text-muted-foreground">{item.expiry_date || "—"}</div></TableCell>
                                            <TableCell>{item.storage_location || "Unassigned"}</TableCell>
                                            <TableCell>{item.supplier || "—"}</TableCell>
                                            <TableCell className="text-right"><div className="flex flex-wrap justify-end gap-2">
                                                <Button size="sm" variant="outline" onClick={() => setQrPreview({ code: item.sku || item.id, title: item.name, subtitle: `${item.category} · ${item.storage_location || "No location"}` })}><Barcode className="w-3.5 h-3.5 mr-1" />{i18n.t("batch2.45e7a1c379c7")}</Button>
                                                {canManageInventory ? <Button size="sm" variant="outline" onClick={() => setItemDlg(item)}>{i18n.t("batch2.5301648dcf6b")}</Button> : null}
                                                {deleteButton(item)}
                                            </div></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="medicine" className="space-y-4">
                    <Card className="p-4">
                        <div className="font-semibold mb-3">{i18n.t("batch2.6ad8acc1a379")}</div>
                        {medicineItems.length === 0 ? <EmptyState title={i18n.t("batch2.7f606a9b1522")} description={i18n.t("batch2.3eb1b2e6c5fb")} /> : (
                            <Table>
                                <TableHeader><TableRow><TableHead>{i18n.t("batch2.709a23220f2c")}</TableHead><TableHead>{i18n.t("batch2.3deb74565196")}</TableHead><TableHead className="text-right">{i18n.t("batch2.1e5ff9e500c2")}</TableHead><TableHead className="text-right">{i18n.t("batch2.8dce170de238")}</TableHead><TableHead>{i18n.t("batch2.ba8f571e0f93")}</TableHead><TableHead>{i18n.t("batch2.d219c68101f5")}</TableHead><TableHead /></TableRow></TableHeader>
                                <TableBody>
                                    {medicineItems.map((item) => (
                                        <TableRow key={item.id}>
                                            <TableCell className="font-semibold">{item.name}</TableCell>
                                            <TableCell><Badge variant="outline">{item.category}</Badge></TableCell>
                                            <TableCell className="text-right font-mono">{formatNumber(item.quantity)} {item.unit}</TableCell>
                                            <TableCell className="text-right font-mono">{cur(item.inventory_value)}</TableCell>
                                            <TableCell>{item.expiry_date || "—"}</TableCell>
                                            <TableCell>{item.storage_location || "Unassigned"}</TableCell>
                                            <TableCell className="text-right"><div className="flex flex-wrap justify-end gap-2"><Button size="sm" variant="outline" onClick={() => { setQrPreview({ code: item.sku || item.id, title: item.name, subtitle: `${item.category} · ${item.storage_location || "No location"}` }); setQrMode("qr"); setTab("qr"); }}>{i18n.t("batch2.45e7a1c379c7")}</Button>{canManageInventory ? <Button size="sm" variant="outline" onClick={() => setItemDlg(item)}>{i18n.t("batch2.5301648dcf6b")}</Button> : null}{deleteButton(item)}</div></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="equipment" className="space-y-4">
                    <Card className="p-4">
                        <div className="font-semibold mb-3">{i18n.t("batch2.7d9252d939e7")}</div>
                        {equipmentItems.length === 0 ? <EmptyState title={i18n.t("batch2.9ae2c512e27d")} description={i18n.t("batch2.ea43c011ff1e")} /> : (
                            <Table>
                                <TableHeader><TableRow><TableHead>{i18n.t("batch2.709a23220f2c")}</TableHead><TableHead>{i18n.t("batch2.d59192b9c8f2")}</TableHead><TableHead className="text-right">{i18n.t("batch2.1e5ff9e500c2")}</TableHead><TableHead className="text-right">{i18n.t("batch2.8dce170de238")}</TableHead><TableHead>{i18n.t("batch2.55edd462872a")}</TableHead><TableHead>{i18n.t("batch2.d219c68101f5")}</TableHead><TableHead /></TableRow></TableHeader>
                                <TableBody>
                                    {equipmentItems.map((item) => (
                                        <TableRow key={item.id}>
                                            <TableCell className="font-semibold">{item.name}</TableCell>
                                            <TableCell className="font-mono text-xs">{item.sku || "—"}</TableCell>
                                            <TableCell className="text-right font-mono">{formatNumber(item.quantity)} {item.unit}</TableCell>
                                            <TableCell className="text-right font-mono">{cur(item.inventory_value)}</TableCell>
                                            <TableCell>{item.supplier || "—"}</TableCell>
                                            <TableCell>{item.storage_location || "Unassigned"}</TableCell>
                                            <TableCell className="text-right"><div className="flex flex-wrap justify-end gap-2"><Button size="sm" variant="outline" onClick={() => { setQrPreview({ code: item.sku || item.id, title: item.name, subtitle: `${item.category} · ${item.storage_location || "No location"}` }); setQrMode("qr"); setTab("qr"); }}>{i18n.t("batch2.45e7a1c379c7")}</Button>{canManageInventory ? <Button size="sm" variant="outline" onClick={() => setItemDlg(item)}>{i18n.t("batch2.5301648dcf6b")}</Button> : null}{deleteButton(item)}</div></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="tools" className="space-y-4">
                    <Card className="p-4">
                        <div className="font-semibold mb-3">{i18n.t("batch2.19ddafc12f92")}</div>
                        {toolsItems.length === 0 ? <EmptyState title={i18n.t("batch2.afe080d91852")} description={i18n.t("batch2.3467859735eb")} /> : (
                            <Table>
                                <TableHeader><TableRow><TableHead>{i18n.t("batch2.709a23220f2c")}</TableHead><TableHead>{i18n.t("batch2.a3c686e711e4")}</TableHead><TableHead className="text-right">{i18n.t("batch2.1e5ff9e500c2")}</TableHead><TableHead className="text-right">{i18n.t("batch2.8dce170de238")}</TableHead><TableHead>{i18n.t("batch2.d219c68101f5")}</TableHead><TableHead /></TableRow></TableHeader>
                                <TableBody>
                                    {toolsItems.map((item) => (
                                        <TableRow key={item.id}>
                                            <TableCell className="font-semibold">{item.name}</TableCell>
                                            <TableCell><Badge variant="outline">{item.category}</Badge></TableCell>
                                            <TableCell className="text-right font-mono">{formatNumber(item.quantity)} {item.unit}</TableCell>
                                            <TableCell className="text-right font-mono">{cur(item.inventory_value)}</TableCell>
                                            <TableCell>{item.storage_location || "Unassigned"}</TableCell>
                                            <TableCell className="text-right"><div className="flex flex-wrap justify-end gap-2"><Button size="sm" variant="outline" onClick={() => { setQrPreview({ code: item.sku || item.id, title: item.name, subtitle: `${item.category} · ${item.storage_location || "No location"}` }); setQrMode("qr"); setTab("qr"); }}>{i18n.t("batch2.45e7a1c379c7")}</Button>{canManageInventory ? <Button size="sm" variant="outline" onClick={() => setItemDlg(item)}>{i18n.t("batch2.5301648dcf6b")}</Button> : null}{deleteButton(item)}</div></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="assets" className="space-y-4">
                    {canManageAssets ? null : <Card className="p-4 border-amber-500/30 bg-amber-500/5"><div className="font-semibold text-amber-700">{i18n.t("batch2.8ac44bc6683d")}</div><div className="text-sm text-muted-foreground mt-1">{i18n.t("batch2.b2107cb53b71")}</div></Card>}
                    <Card className="p-4">
                        <div className="font-semibold mb-3">{i18n.t("batch2.d356631d4242")}</div>
                        {assetRows.length === 0 ? <EmptyState title={i18n.t("batch2.9a4352f7fb4a")} description={i18n.t("batch2.493a215189e8")} /> : (
                            <Table>
                                <TableHeader><TableRow><TableHead>{i18n.t("batch2.709a23220f2c")}</TableHead><TableHead>{i18n.t("batch2.3deb74565196")}</TableHead><TableHead>{i18n.t("batch2.bae7d5be7082")}</TableHead><TableHead>{i18n.t("batch2.2f4979324e03")}</TableHead><TableHead>{i18n.t("batch2.d219c68101f5")}</TableHead><TableHead>{i18n.t("batch2.8fba6b663578")}</TableHead><TableHead /></TableRow></TableHeader>
                                <TableBody>
                                    {assetRows.map((asset) => (
                                        <TableRow key={asset.id}>
                                            <TableCell className="font-semibold">{asset.name}</TableCell>
                                            <TableCell><Badge variant="outline">{asset.type || "other"}</Badge></TableCell>
                                            <TableCell>{asset.status || "—"}</TableCell>
                                            <TableCell>{asset.condition || "—"}</TableCell>
                                            <TableCell>{asset.location || asset.storage_location || "—"}</TableCell>
                                            <TableCell>{asset.next_service_date || "—"}</TableCell>
                                            <TableCell className="text-right space-x-2"><Button size="sm" variant="outline" data-testid={`asset-qr-btn-${asset.id}`} onClick={() => { setQrPreview({ code: asset.qr_code || asset.qrCode || asset.id, title: asset.name, subtitle: `${asset.type || "asset"} · ${asset.status || "unknown"}` }); setQrMode("qr"); setTab("qr"); }}>{i18n.t("batch2.45e7a1c379c7")}</Button>{canManageAssets ? <Button size="sm" variant="outline" data-testid={`edit-asset-btn-${asset.id}`} onClick={() => setAssetDlg(asset)}>{i18n.t("batch2.5301648dcf6b")}</Button> : null}{canManageAssets ? <Button size="sm" variant="destructive" data-testid={`delete-asset-btn-${asset.id}`} onClick={() => deleteAsset(asset)}>{i18n.t("batch2.f6fdbe48dc54")}</Button> : null}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="locations" className="space-y-4">
                    <Card className="p-4">
                        <div className="font-semibold mb-3">{i18n.t("batch2.1d0c73d85f54")}</div>
                        {locations.length === 0 ? <EmptyState title={i18n.t("batch2.0b06e22a7e19")} description={i18n.t("batch2.460faa8a18da")} /> : (
                            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                                {locations.map((location) => (
                                    <Card key={location.name} className="p-4">
                                        <div className="font-semibold truncate">{location.name}</div>
                                        <div className="mt-3 text-sm text-muted-foreground flex items-center justify-between"><span>{i18n.t("batch2.44d25b5d1b6d")}</span><span className="font-semibold text-foreground">{location.items}</span></div>
                                        <div className="mt-2 text-sm text-muted-foreground flex items-center justify-between"><span>{i18n.t("batch2.bcecf4562f17")}</span><span className="font-semibold text-foreground">{formatNumber(location.stock)}</span></div>
                                        <div className="mt-2 text-sm text-muted-foreground flex items-center justify-between"><span>{i18n.t("batch2.8dce170de238")}</span><span className="font-semibold text-foreground">{cur(location.value)}</span></div>
                                        <div className="mt-2 text-sm text-muted-foreground flex items-center justify-between"><span>{i18n.t("batch2.a6e1fef14e1b")}</span><span className="font-semibold text-destructive">{location.low}</span></div>
                                        <div className="mt-2 text-sm text-muted-foreground flex items-center justify-between"><span>{i18n.t("batch2.8df6fbb79f88")}</span><span className="font-semibold text-amber-600">{location.expiring}</span></div>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="movements" className="space-y-4">
                    <Card className="p-4">
                        <div className="font-semibold mb-3">{i18n.t("batch2.1e75cf7a0372")}</div>
                        {movementRows.length === 0 ? <EmptyState title={i18n.t("batch2.2bee5f4a4c10")} description={i18n.t("batch2.93ccf8886685")} /> : (
                            <Table>
                                <TableHeader><TableRow><TableHead>{i18n.t("batch2.eb9a4bc1c0c1")}</TableHead><TableHead>{i18n.t("batch2.3deb74565196")}</TableHead><TableHead>{i18n.t("batch2.ecdda59aea5e")}</TableHead><TableHead className="text-right">{i18n.t("batch2.1e5ff9e500c2")}</TableHead><TableHead>{i18n.t("batch2.6da13addb000")}</TableHead><TableHead>{i18n.t("batch2.70440046a3dc")}</TableHead></TableRow></TableHeader>
                                <TableBody>
                                    {movementRows.map((movement) => (
                                        <TableRow key={movement.id}>
                                            <TableCell>{movement.date || "—"}</TableCell>
                                            <TableCell><Badge variant="outline">{movement.type || movement.movement_type || "—"}</Badge></TableCell>
                                            <TableCell>{movement.item_name || movement.item_id || "—"}</TableCell>
                                            <TableCell className="text-right font-mono">{formatNumber(movement.quantity || movement.delta || 0)} {movement.unit || ""}</TableCell>
                                            <TableCell className="text-xs">{movement.source_module || "inventory"}</TableCell>
                                            <TableCell className="text-xs">{movement.notes || "—"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="qr" className="space-y-4">
                    <Card className="p-4">
                        <div className="grid gap-3 md:grid-cols-[1fr_auto] items-end">
                            <div>
                                <Label>{i18n.t("batch2.0a69d61b0b25")}</Label>
                                <Input value={qrQuery} onChange={(e) => setQrQuery(e.target.value)} className="h-11 mt-1" placeholder={i18n.t("batch2.aa68ea8175f6")} data-testid="qr-search-input" />
                            </div>
                            <Button variant="outline" className="h-11" onClick={() => setQrLookup(selectedQrData)}><ScanSearch className="w-4 h-4 mr-2" />{i18n.t("batch2.7015ab4fc4ea")}</Button>
                        </div>
                        <div className="mt-4 grid gap-4 lg:grid-cols-2">
                            <Card className="p-4">
                                <div className="font-semibold mb-3">{i18n.t("batch2.bb98ca672621")}</div>
                                {qrPreview ? (
                                    <div className="grid place-items-center gap-3 text-center">
                                        <div className="inline-flex rounded-md border bg-muted/30 p-1">
                                            <Button size="sm" variant={qrMode === "qr" ? "default" : "ghost"} onClick={() => setQrMode("qr")}>{i18n.t("batch2.7db486db207f")}</Button>
                                            <Button size="sm" variant={qrMode === "barcode" ? "default" : "ghost"} onClick={() => setQrMode("barcode")}>{i18n.t("batch2.2eed73e61b10")}</Button>
                                        </div>
                                        {qrMode === "qr" ? (
                                            <QRCodeSVG id="inventory-qr-svg" value={qrPreview.code} size={220} includeMargin />
                                        ) : (
                                            <img id="inventory-barcode-img" src={barcodeImageUrl(qrPreview.code)} alt={i18n.t("batch2.8fabfed883f0")} className="max-w-full" />
                                        )}
                                        <div className="font-semibold">{qrPreview.title}</div>
                                        <div className="text-sm text-muted-foreground">{qrPreview.subtitle}</div>
                                        <div className="font-mono text-xs">{qrPreview.code}</div>
                                        <div className="flex flex-wrap justify-center gap-2">
                                            <Button variant="outline" onClick={() => printCode(qrPreview, qrMode)}><Barcode className="w-4 h-4 mr-2" />{i18n.t("batch2.5b221e9c2a45")}</Button>
                                            <Button variant="outline" onClick={downloadCodePng}>{i18n.t("batch2.0c8309d1f4d0")}</Button>
                                            <Button variant="outline" onClick={downloadCodePdf}>{i18n.t("batch2.98e5ef062b2a")}</Button>
                                        </div>
                                    </div>
                                ) : <EmptyState title={i18n.t("batch2.7e8892b35c5f")} description={i18n.t("batch2.145f77316273")} />}
                            </Card>
                            <Card className="p-4">
                                <div className="font-semibold mb-3">{i18n.t("batch2.a7fc7eb3b9ba")}</div>
                                {qrLookup ? (
                                    <div className="space-y-2 text-sm">
                                        <div className="font-semibold">{qrLookup.title}</div>
                                        <div className="text-muted-foreground">{qrLookup.subtitle}</div>
                                        <div className="font-mono">{i18n.t("batch2.ca567dda33cd")} {qrLookup.code}</div>
                                    </div>
                                ) : <EmptyState title={i18n.t("batch2.d82998b02298")} description={i18n.t("batch2.58c0bd6ade68")} />}
                            </Card>
                        </div>
                    </Card>
                </TabsContent>
            </Tabs>

            <Card className="p-4">
                <div className="font-semibold mb-3">{i18n.t("batch2.fd3bc9709730")}</div>
                <div className="grid gap-3 md:grid-cols-4 text-sm">
                    <div><span className="font-semibold">{i18n.t("batch2.1c26dccca4ec")}</span> {formatNumber(metrics.feed_usage_this_month || 0)}</div>
                    <div><span className="font-semibold">{i18n.t("batch2.54bf2f40c6da")}</span> {formatNumber(metrics.medicine_usage_this_month || 0)}</div>
                    <div><span className="font-semibold">{i18n.t("batch2.b2d28451f2ac")}</span> {formatNumber(metrics.inventory_consumption_this_month || 0)}</div>
                    <div><span className="font-semibold">{i18n.t("batch2.76e4488c60b0")}</span> {cur(metrics.total_inventory_value || 0)}</div>
                </div>
            </Card>

            <AlertDialog open={Boolean(deleteItemTarget)} onOpenChange={(open) => { if (!open && !deletingItem) setDeleteItemTarget(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete inventory item &quot;{deleteItemTarget?.name}&quot;?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes the item from active inventory and its generated stock and expiry alerts. The action is permanent in the active inventory view and will be blocked if protected historical records depend on this item.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deletingItem}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(event) => { event.preventDefault(); confirmDeleteItem(); }}
                            disabled={deletingItem}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deletingItem ? "Deleting…" : "Delete Item"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
